import type { BrandModule } from "./types.js";

export const DEFAULT_BRAND_KEY = "startle";

type BrandLoader = () => Promise<{ default: BrandModule }>;

/**
 * Keyed on `brandKey`, which `useBrand()` in `@touchscreen/hooks` resolves from `venue.brand_key`
 * by default. `useBrand()` also honours an explicit `?brand=` URL override on top of that — a
 * deliberate escape hatch to force/preview a theme regardless of what the venue resolves to (see
 * that hook's comment). This registry doesn't care which of the two produced the key it's given.
 * Extend this map as real brand packages land under `brands/`. Unknown keys, and any load
 * failure, fall back to the default brand so a venue never renders broken because a brand build
 * hasn't shipped yet.
 */
const brandLoaders: Record<string, BrandLoader> = {
  [DEFAULT_BRAND_KEY]: () => import("@touchscreen/brand-startle"),
  santander: () => import("@touchscreen/brand-santander"),
};

export async function loadBrand(brandKey: string): Promise<BrandModule> {
  const loader = brandLoaders[brandKey];
  if (!loader) {
    return loadDefaultBrand();
  }
  try {
    const mod = await loader();
    return mod.default;
  } catch {
    return loadDefaultBrand();
  }
}

async function loadDefaultBrand(): Promise<BrandModule> {
  const mod = await brandLoaders[DEFAULT_BRAND_KEY]!();
  return mod.default;
}
